//#region Imports and Global Definitions
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { StyleSheet, View, Modal, SafeAreaView, FlatList, Text, Platform, ScrollView, useWindowDimensions, TouchableOpacity } from 'react-native';
import { useTheme } from '@react-navigation/native';
import { useForm, useWatch, watch } from 'react-hook-form';
import { useAccountContext } from '../../../hooks/useAccountContext';
import { createCommonStyles } from '../../../styles/commonStyles';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomControlledInput from '../../../components/ControlledComponents/CustomControlledInput';
import CustomControlledDropdown from '../../../components/ControlledComponents/CustomControlledDropdown';
import CustomButton from '../../../components/CustomButton';
import CustomCalendar from '../../../components/CustomCalendar/CustomCalendar';
import CustomInput from '../../../components/CustomInput';
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import CustomControlledCheckbox from '../../../components/ControlledComponents/CustomControlledCheckbox';
import { showMessage } from "react-native-flash-message";
import CustomExpandableView from '../../../components/CustomExpandableView/CustomExpandableView';
import { useTranslation } from 'react-i18next';
import { useTraining } from '../../../hooks/useTraining';
import { useSettingsContext } from '../../../hooks/useSettingsContext';
import { useHRAdminEmployees } from '../../../hooks/useHRAdminEmployees';
import { ActivityIndicator, Card } from 'react-native-paper';
import { generateUniqueID } from '../../../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions';
import { Checkbox } from 'react-native-paper';
import HRAdminTrainingParticipantsScreen from './HRAdminParticipantsScreen';
import { formatDateForUI } from '../../../constants/FormatDate';
import HRAdminTrainingModulesScreen from './HRAdminTrainingModulesScreen';
import HRAdminPrerequisitesScreen from './HRAdminPrerequisitesScreen';
import HRAdminDocFormScreen from './HRAdminDocFormScreen';
import Swal from 'sweetalert2';
import { useTableHelper } from '../../../hooks/useTableHelper';
//#endregion

//#region Helpers
const GetFieldComponent = (
    totalPart,
    fieldType,
    t,
    getValues,
    field,
    setValue,
    getDropdownValues,
    renameKeysInOptions,
    control,
    employeeId,
    styles,
    colors,
) => {
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedData, setSelectedData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const startDate = useWatch({ control, name: 'startDate' });
    const startDateOnLoad = useState(formatDateForUI(getValues("startDate")) || "");
    const endDate = useWatch({ control, name: 'endDate' });
    const endDateOnLoad = useState(formatDateForUI(getValues("endDate")) || "");
    const watchField = useWatch({ control, name: field[0] });

    // Watch company and branch fields
    const company = useWatch({ control, name: 'company' });
    const category = useWatch({ control, name: 'category' });

    /* state variables to handle toggling dropdown and textbox */
    const [trainingProviderEditMode, setTrainingProviderEditMode] = useState(false);
    const [locationEditMode, setLocationEditMode] = useState(false);

    /* toggle functions to handle change of input */
    const toggleTrainingProviderInputBox = () => {
        setTrainingProviderEditMode(prev => !prev);
    };

    const toggleLocationInputBox = () => {
        setLocationEditMode(prev => !prev);
    };


    //#region Calendar
    useEffect(() => {
        if (!endDate || !startDate) return;
        if (endDate === (endDateOnLoad ? endDateOnLoad[0].toString() : '')) {
            if (startDate === startDateOnLoad[0].toString())
                return;
        }
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);
        const currentDateObj = new Date();
        if ((startDateObj <= currentDateObj) && (endDateObj > currentDateObj || !endDate)) {
            setValue('active', true);
        } else
            setValue('active', false);
    }, [startDate, endDate])

    const onShowCalendarPress = () => {
        setShowCalendar(!showCalendar);
        if (showCalendar) {
            setValue(field[0], formatDateForUI(selectedData.startDate));
        }
    }
    //#endregion

    //#region Change Dropdowns
    useEffect(() => {
        if (field[0] !== 'branch') return;
        changeDropdownValues('branch');
    }, [company]);

    useEffect(() => {
        if (field[0] !== 'subject') return;
        changeDropdownValues('subject');
    }, [category]);

    function changeDropdownValues(dropdown) {
        let sParams = [];
        switch (dropdown) {
            case 'branch':
                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { branch: { sFilt1: company, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.branch);
                    setIsLoading(false);
                });
                break;
            case 'subject':
                if (!category) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { subject: { sFilt1: category, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.subject);
                    setIsLoading(false);
                });
                break;
            default:
                break;
        }
    }
    //#endregion

    // Load dropdown options for company
    useEffect(() => {
        if (field[0] !== 'company') return;

        // Fetch company dropdown values
        setIsLoading(true);
        getDropdownValues([{ company: { sFilt1: "", sFilt2: employeeId, sFilt3: "" } }])
            .then((res) => {
                field[1].options = renameKeysInOptions(res?.company);
            })
            .finally(() => setIsLoading(false));
    }, [field, getDropdownValues, renameKeysInOptions]);

    // Load dropdown options for branch (only after company selected)
    useEffect(() => {
        if (field[0] !== 'branch') return;

        if (!company) {
            // Clear branch options if no company selected
            field[1].options = [];
            return;
        }

        const sParams = [{ branch: { sFilt1: company, sFilt2: "", sFilt3: "" } }];

        setIsLoading(true);
        getDropdownValues(sParams)
            .then((res) => {
                field[1].options = renameKeysInOptions(res?.branch);
            })
            .finally(() => setIsLoading(false));
    }, [company, field, getDropdownValues, renameKeysInOptions]);

    //#region Autocalculated
    useEffect(() => {
        if (totalPart !== 0) {
            let fees = Number(getValues('fees')) || 0;
            let subsidy = Number(getValues('subsidy')) || 0;
            let othercosts = Number(getValues('othercosts')) || 0;

            let totalCosts = fees + othercosts - subsidy;
            const costPerEmployee = (totalCosts / totalPart).toFixed(2);
            setValue('costperempl', costPerEmployee);
        }
    }, [getValues("othercosts"), getValues("subsidy"), getValues("fees"), getValues('costperempl')]);

    switch (fieldType) {
        case 'dropdown':
            return (
                <View style={styles.box}>
                    <View style={styles.leftColumn}>
                        <CustomLabel
                            value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                            type='EDIT_EMPLOYEE_HEADER'
                        />
                    </View>

                    <View style={[styles.middleColumn]}>
                        {!isLoading ? (
                            field[0] === 'trainingProvider' ? (
                                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    <View style={{ flex: 1 }}>
                                        {trainingProviderEditMode ? (
                                            <CustomInput
                                                value={getValues('trainingProvider') || ''}
                                                setValue={(text) => setValue('trainingProvider', text)}
                                                editable={!field[1]?.isDisabled}
                                                maxLen={100}
                                                type="MAININPUT"
                                            />
                                        ) : (
                                            <CustomControlledDropdown
                                                multipleSelect={false}
                                                control={control}
                                                name={field[0]}
                                                type={((field[1]?.required && (watchField?.length <= 0 || watchField === undefined))
                                                    || field[1]?.error) ? 'EDIT_EMPLOYEE_REQUIRED' : 'EDIT_EMPLOYEE'}
                                                options={field[1]?.options}
                                                setValue={setValue}
                                                disabled={field[1]?.isDisabled}
                                                rules={field[1]}
                                            />
                                        )}
                                    </View>
                                    {!field[1]?.isDisabled && (
                                        <TouchableOpacity
                                            onPress={toggleTrainingProviderInputBox}
                                            style={{ paddingTop: 10, marginLeft: 8 }}
                                        >
                                            <Ionicons name="text-outline" size={24} color={colors.text_primary} />
                                        </TouchableOpacity>
                                    )}
                                </View>
                            ) : field[0] === 'trainingLocation' ? (
                                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    <View style={{ flex: 1 }}>
                                        {locationEditMode ? (
                                            <CustomInput
                                                value={getValues('trainingLocation') || ''}
                                                setValue={(text) => setValue('trainingLocation', text)}
                                                editable={!field[1]?.isDisabled}
                                                maxLen={100}
                                                type="MAININPUT"
                                            />
                                        ) : (
                                            <CustomControlledDropdown
                                                multipleSelect={false}
                                                control={control}
                                                name={field[0]}
                                                type={((field[1]?.required && (watchField?.length <= 0 || watchField === undefined))
                                                    || field[1]?.error) ? 'EDIT_EMPLOYEE_REQUIRED' : 'EDIT_EMPLOYEE'}
                                                options={field[1]?.options}
                                                setValue={setValue}
                                                disabled={field[1]?.isDisabled}
                                                rules={field[1]}
                                            />
                                        )}
                                    </View>
                                    {!field[1]?.isDisabled && (
                                        <TouchableOpacity
                                            onPress={toggleLocationInputBox}
                                            style={{ paddingTop: 10, marginLeft: 8 }}
                                        >
                                            <Ionicons name="text-outline" size={24} color={colors.text_primary} />
                                        </TouchableOpacity>
                                    )}
                                </View>
                            ) : (
                                <CustomControlledDropdown
                                    multipleSelect={false}
                                    control={control}
                                    name={field[0]}
                                    type={((field[1]?.required && (watchField?.length <= 0 || watchField === undefined))
                                        || field[1]?.error) ? 'EDIT_EMPLOYEE_REQUIRED' : 'EDIT_EMPLOYEE'}
                                    options={field[1]?.options}
                                    setValue={setValue}
                                    disabled={field[1]?.isDisabled}
                                    rules={field[1]}
                                />
                            )
                        ) : (
                            <CustomLabel
                                value={t('common:loading')}
                                type='EDIT_EMPLOYEE_LOADING'
                            />
                        )}
                    </View>

                    <View style={styles.rightColumn}>
                        {((field[1]?.required && (watchField?.length <= 0 || watchField === undefined)) || field[1]?.error) &&
                            <CustomLabel
                                value="*"
                                type='error'
                            />
                        }
                    </View>
                </View>
            )
        case 'autocalculated':
            return (
                <View style={styles.box}>
                    <View style={styles.leftColumn}>
                        <CustomLabel
                            value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                            type='EDIT_EMPLOYEE_HEADER'
                        />
                    </View>
                    <View style={styles.middleColumn}>
                        <CustomControlledInput
                            name={field[0]}
                            control={control}
                            editable={false}
                            rules={field[1]}
                            type='MAININPUT'
                        />
                    </View>
                    <View style={styles.rightColumn}></View>
                </View>
            )
        case 'checkbox':
            return (
                <View style={styles.box}>
                    <View style={styles.leftColumn}>
                        {field[0] === 'isabroad' && process.env.REACT_APP_CLIENT === 'atlas' ? (
                            <CustomLabel
                                value={t(`common:openseminar`)}
                                type='EDIT_EMPLOYEE_HEADER'
                            />
                        ) : (
                        <CustomLabel
                            value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                            type='EDIT_EMPLOYEE_HEADER'
                        />)}
                    </View>
                    <View style={styles.middleColumn}>
                        <CustomControlledCheckbox
                            control={control}
                            name={field[0]}
                            type="EDIT_EMPLOYEE"
                        />
                    </View>
                    <View style={styles.rightColumn}></View>
                </View>
            )
        case 'date':
            return (
                <View>
                    <SafeAreaView style={styles.box}>
                        <View style={styles.leftColumn}>
                            <CustomLabel
                                value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                                type='EDIT_EMPLOYEE_HEADER'
                            />
                        </View>
                        <View style={styles.middleColumn}>
                            <CustomControlledInput
                                name={field[0]}
                                control={control}
                                editable={false}
                                rules={field[1]}
                                type={((field[1]?.required && (watchField?.length <= 0 || watchField === undefined))
                                    || field[1]?.error) ? 'EDIT_EMPLOYEE_REQUIRED' : 'MAININPUT'} />
                        </View>
                        <View style={[styles.rightColumn, { flexDirection: 'row' }]}>
                            {!field[1]?.isDisabled &&
                                <Ionicons
                                    name={showCalendar ? "calendar" : "calendar-outline"}
                                    style={styles.icons}
                                    onPress={() => onShowCalendarPress(field[0])}
                                />
                            }
                            {field[1]?.required &&
                                <CustomLabel
                                    value="*"
                                    type='error'
                                />
                            }
                        </View>
                    </SafeAreaView>
                    {showCalendar &&
                        <SafeAreaView>
                            <View style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: 10,
                                borderWidth: 0,
                                borderColor: colors.border_card,
                            }}>
                                <CustomCalendar
                                    calendarType={'RangePicker'}
                                    setRange={setSelectedData}
                                    range={selectedData}
                                    allowSingleDate={true}
                                    startingYear={1950}
                                />
                            </View>
                        </SafeAreaView>
                    }
                </View>
            )
        case 'divider':
            return (
                <View style={{ marginTop: '2%' }}>
                    <CustomLabel
                        value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                        type='EDIT_EMPLOYEE_DIVIDER'
                    />
                    <SafeAreaView style={styles.horizontalLine} />
                </View>
            )
        default:
            return (
                <View style={styles.box}>
                    <View style={styles.leftColumn}>
                        <CustomLabel
                            value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                            type='EDIT_EMPLOYEE_HEADER'
                        />
                    </View>
                    <View style={styles.middleColumn}>
                        <CustomControlledInput
                            name={field[0]}
                            control={control}
                            editable={field[1]?.isDisabled ? false : true}
                            rules={field[1]}
                            type={((field[1]?.required && (watchField?.length <= 0 || watchField === undefined))
                                || field[1]?.error) ? 'EDIT_EMPLOYEE_REQUIRED' : 'MAININPUT'}
                        />
                    </View>
                    <View style={styles.rightColumn}>
                        {((field[1]?.required && (watchField?.length <= 0 || watchField === undefined)) || field[1]?.error) &&
                            <CustomLabel
                                value="*"
                                type='error'
                            />
                        }
                    </View>
                </View>
            )
    }
};

const DisplayField = React.memo(({ totalPart, styles, colors, field, control, t, getValues, setValue, watch, getDropdownValues, renameKeysInOptions, employeeId
}) => {
    if (field[1]?.isVisible === false) return;
    let types = Array.isArray(field[1]?.type) ? field[1]?.type : [field[1]?.type ? field[1]?.type : ''];

    return (
        field[1]?.isVisible ? <SafeAreaView key={field[0]}>
            {types.length >= 2 ? (
                <View style={styles.row}>
                    <View style={styles.leftColumn}>
                        <CustomLabel
                            value={field[1]?.label ? t(`common:${field[1]?.label}`) : t(`common:${field[0]}`)}
                            type='EDIT_EMPLOYEE'
                        />
                    </View>
                    <View style={[styles.middleColumn, { flexDirection: 'row', alignItems: 'center' }]}>
                        {types.map((fieldType, index) => (
                            GetFieldComponent(totalPart, fieldType, t, getValues, field, setValue, getDropdownValues, renameKeysInOptions, control, employeeId, styles, colors)
                        ))}
                    </View>
                    <View style={styles.rightColumn}></View>
                </View>
            ) : (
                types.map(fieldType => (
                    GetFieldComponent(totalPart, fieldType, t, getValues, field, setValue, getDropdownValues, renameKeysInOptions, control, employeeId, styles, colors)
                ))
            )}
        </SafeAreaView> : <></>

    );
});

//#region CustomSectionButton
const MemoizedCustomButton = React.memo(({ onPress, t, text, setExpanded, isExpanded, styles }) => {
    return (
        <CustomButton
            text={text}
            onPress={() => onPress(setExpanded)}
            type="EDIT_EMPLOYEE_EXPANDABLE"
            iconName={isExpanded ? "chevron-down-outline" : "chevron-forward-outline"}
            show="EDIT_EMPLOYEE_EXPANDABLE"
            textTop="0"
            sz={25}
        />
    );
});
//#endregion

//#region Render
const Render = React.memo(({ render, arr, styles }) => {
    const visibleItems = arr.filter(item => item[1].isVisible === true);
    return (
        <SafeAreaView style={[styles.row, { height: '100%', }]}>
            {/* left column */}
            <SafeAreaView style={[styles.column]}>
                {render(visibleItems.slice(0, Math.ceil(visibleItems.length / 2)))}
            </SafeAreaView>

            {/* middle column */}
            <SafeAreaView style={styles.verticalLine} />

            {/* right column */}
            <SafeAreaView style={styles.column}>
                {render(visibleItems.slice(Math.ceil(visibleItems.length / 2)))}
            </SafeAreaView>
        </SafeAreaView>
    )
});

const OrganizersList = React.memo(({ item, index, styles, common, employeeOptions,
    handleEditPress, handleDelete, isEvent
}) => {
    return (
        <Card style={[common.cardstyle, { margin: 10 }]} >
            <Card.Content>
                <SafeAreaView>
                    <SafeAreaView style={styles.rowOrg}>
                        <View style={[styles.itemOrg]}>
                            <CustomDropDown
                                disabled={isEvent}
                                defValue={item.facilitatorId}
                                options={employeeOptions}
                                value={item.facilitatorId}
                                setValue={(e) => { handleEditPress(index, item, 'facilitatorId', e) }}
                                type="ASSESSMENT"
                            />
                        </View>
                        <CustomButton
                            iconName='trash-outline'
                            onPress={() => { handleDelete(item, index, 'rowguid') }}
                            show='HOME_BELL'
                            type='HOME_BELL'
                            textTop='2'
                            sz={30} />
                    </SafeAreaView>
                </SafeAreaView>
            </Card.Content>
        </Card>
    );
});
//#endregion

//#endregion

const HRAdminTrainingDetailsScreen = (props) => {
    //#region Const Definitions
    const ref = useRef(null);
    const { account } = useAccountContext();
    const { t, i18n } = useTranslation();
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height, isPlatformWeb);

    const { selectedTraining, setSelectedTraining, isEdit, setIsEdit, trnOptions, trnData } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [refreshAfterSave, setRefreshAfterSave] = useState(false);
    const [dropdowns, setDropdowns] = useState([]);
    const [totalPart, setTotalPart] = useState(0);
    const [originalUserData, setOriginalUserData] = useState({});
    const [originalPartData, setOriginalPartData] = useState([]);
    const [courseDetailsTabArray, setCourseDetailsTabArray] = useState([]);

    const { loadTrainingDetails, loadTrainingHelpers, saveTrainingData, loadParticipants,
        totalParticipants, loadEvaluationForms, SaveEvaluationForm, DeleteEvaluationForm,
        loadTrainingMaterial, SaveTrainingMaterial, DeleteTrainingMaterial, loadEvent,
        loadEventOrganizers, saveTRNEvent, saveEventOrganizers, deleteParticipantsFromTRNUI
    } = useTraining(); // Get trainings data
    const { getDropdownValues, getEmployeesInCompany } = useHRAdminEmployees(); // Get dropdown helpers
    const { courseDetailsTabData, participantsTabData } = useSettingsContext(); // Get training fielda

    const { control, setValue, handleSubmit, getValues, register } = useForm({
        defaultValues: useMemo(() => ({
            ...originalUserData
        }), [originalUserData])
    });

    //#region ExpandedVariables
    const [isCourseExpanded, setIsCourseExpanded] = useState(false);
    const [isParticipantsExpanded, setIsParticipantsExpanded] = useState(false);
    const [isModulesExpanded, setIsModulesExpanded] = useState(false);
    const [isPrerequisitesExpanded, setIsPrerequisitesExpanded] = useState(false);
    const [isEvaluationFormExpanded, setIsEvaluationFormExpanded] = useState(false);
    const [isTrainingMaterialExpanded, setIsTrainingMaterialExpanded] = useState(false);
    const toggleTab = (tabName) => {
        if (tabName === 'course') {
            setIsModulesExpanded(false);
            setIsCourseExpanded(prev => !prev);
            setIsParticipantsExpanded(false);
            setIsPrerequisitesExpanded(false);
            setIsEvaluationFormExpanded(false);
            setIsTrainingMaterialExpanded(false);
        } else if (tabName === 'participants') {
            setIsModulesExpanded(false);
            setIsParticipantsExpanded(prev => !prev);
            setIsCourseExpanded(false);
            setIsPrerequisitesExpanded(false);
            setIsEvaluationFormExpanded(false);
            setIsTrainingMaterialExpanded(false);
        } else if (tabName === 'modules') {
            setIsModulesExpanded(prev => !prev);
            setIsParticipantsExpanded(false);
            setIsCourseExpanded(false);
            setIsPrerequisitesExpanded(false);
            setIsEvaluationFormExpanded(false);
            setIsTrainingMaterialExpanded(false);
        } else if (tabName === 'prerequisites') {
            setIsModulesExpanded(false);
            setIsParticipantsExpanded(false);
            setIsCourseExpanded(false);
            setIsPrerequisitesExpanded(prev => !prev);
            setIsEvaluationFormExpanded(false);
            setIsTrainingMaterialExpanded(false);
        } else if (tabName === 'evaluationForm') {
            setIsModulesExpanded(false);
            setIsParticipantsExpanded(false);
            setIsCourseExpanded(false);
            setIsPrerequisitesExpanded(false);
            setIsEvaluationFormExpanded(prev => !prev);
            setIsTrainingMaterialExpanded(false);
        } else if (tabName === 'trainingMaterial') {
            setIsModulesExpanded(false);
            setIsParticipantsExpanded(false);
            setIsCourseExpanded(false);
            setIsPrerequisitesExpanded(false);
            setIsEvaluationFormExpanded(false);
            setIsTrainingMaterialExpanded(prev => !prev);
        }
    };
    //#endregion
    //#endregion

    //#region Event Modla
    const { rowEdit, rowDelete, rowAdd, handleCancelChanges, hasDuplicates } = useTableHelper();
    const [modalVisible, setModalVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [organizersData, setOrganizersData] = useState([]);
    const [changes, setChanges] = useState({});
    const [insertData, setInsertData] = useState([]);
    const [deletedItems, setDeletedItems] = useState([]);
    const [eventData, setEventData] = useState({});
    const [isEvent, setIsEvent] = useState(false);

    // Function to open modal
    const openModal = () => {
        if (!isEvent) {
            Swal.fire({
                title: t('common:areusure'),
                text: t('common:deleteAllPartBeforeEvent'),
                icon: "question",
                showCancelButton: true,
                reverseButtons: true,
                cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">Cancel</text>`,
                cancelButtonColor: '#d3d3d3',
                confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">Delete</text>`,
                confirmButtonColor: '#A0A2FF',
            }).then((result) => {
                if (result.isConfirmed) {
                    setModalVisible(true);
                    return;
                }
            });
        } else {
            setModalVisible(true);
        }
    };

    // Function to close modal
    const closeModal = useCallback(() => {
        setErrorMessage('');
        setModalVisible(false);
    }, []);

    // * Render Organizers
    const renderOrganizers = (item, index) => {
        return (
            <OrganizersList
                item={item}
                index={index}
                styles={styles}
                common={common}
                employeeOptions={dropdowns['participants'] || []}
                handleEditPress={handleEditPress}
                handleDelete={handleDelete}
                isEvent={isEvent}
            />
        )
    }

    // * Handle Organizers
    const handleEditPress = useCallback((index, item, key, value) => {
        if (item[key] === value) return;
        rowEdit(index, item, key, value, changes, setChanges, organizersData, setOrganizersData)
    }, []);

    const handleDelete = (item, index, attribute) => {
        rowDelete(item, index, attribute, changes, setChanges, organizersData, setOrganizersData, setDeletedItems, ref)
    }

    const handleAddNew = () => {
        let temp = [...organizersData];
        if (organizersData.length === 0)
            temp = {
                facilitatorId: "",
                rowguid: generateUniqueID(),
            }
        rowAdd(temp, setOrganizersData, ['rowguid'], [], ref, t, [], [], undefined, undefined, insertData, setInsertData, id = 'rowguid',);
    }

    const saveEvent = async () => {
        setErrorMessage('');

        // Check if there are any changes to save
        const isEventDataEmpty = !eventData || Object.keys(eventData).length === 0;
        const isOrganizersEmpty = !organizersData || organizersData.length === 0;

        if (isEventDataEmpty && isOrganizersEmpty) {
            setErrorMessage(t('common:noChanges'));
            return;
        }

        // Validate required fields in eventData
        if (!eventData.title || eventData.title.trim() === '') {
            setErrorMessage(t('common:enterTitle'));
            return;
        }
        if (!eventData.description || eventData.description.trim() === '') {
            setErrorMessage(t('common:enterDescription'));
            return;
        }

        // Validate at least one organizer
        if (isOrganizersEmpty) {
            setErrorMessage(t('common:enterAtLeastOneOrganizer'));
            return;
        }

        setIsLoading(true);

        const eventPayload = {
            ...eventData,
            trnUI: eventData.trnUI || (props.selectedTraining?.trnUI),
        };
        const organizersPayload = organizersData.map(org => ({
            ...org,
            trnUI: eventData.trnUI || (props.selectedTraining?.trnUI),
        }));

        saveTRNEvent(eventPayload).then((eventRes) => {
            if (eventRes === 200) {
                saveEventOrganizers(organizersPayload).then((organizersRes) => {
                    if (organizersRes === 200) {
                        deleteParticipantsFromTRNUI(selectedTraining.trnUI).then((res) => {
                            if (res === 200) {
                                setRefreshAfterSave(true);
                                closeModal();
                                showMessage({
                                    message: t('common:allOperationsSuccMess'),
                                    type: 'success',
                                    duration: 3000,
                                });
                            } else {
                                showMessage({
                                    message: t('common:errorSaving'),
                                    type: 'danger',
                                    duration: 3000,
                                });
                            }
                        }).catch(() => {
                            showMessage({
                                message: t('common:errorSaving'),
                                type: 'danger',
                                duration: 3000,
                            });
                        })

                    } else {
                        setRefreshAfterSave(true);
                        closeModal();
                        showMessage({
                            message: t('common:errorSaving'),
                            type: 'danger',
                            duration: 3000,
                        });
                    }
                }).catch(() => {
                    console.error(error);
                    showMessage({
                        message: t('common:errorSaving'),
                        type: 'danger',
                        duration: 3000,
                    });
                })
            } else {
                setRefreshAfterSave(true);
                closeModal();
                showMessage({
                    message: t('common:errorSaving'),
                    type: 'danger',
                    duration: 3000,
                });
            }
        }).catch(() => {
            showMessage({
                message: t('common:errorSaving'),
                type: 'danger',
                duration: 3000,
            });
        })
        setIsLoading(false);
    };
    //#endregion


    //#region UseEffects
    // Load Data
    useEffect(() => {
        setIsLoading(true);

        if (isEdit) {
            LoadEvent();
            loadTrainingData();
        } else {
            // if new course to create
            const sParams = [
                { company: { sFilt1: "", sFilt2: account.employeeId, sFilt3: "" } },
                { branch: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                { category: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                { subject: { sFilt1: "", sFilt2: "", sFilt3: "" } }
            ];
            let dropdownValues = {};
            getDropdownValues(sParams).then((res) => {
                getEmployeesInCompany("").then((empl) => {
                    let renamedField = renameKeysInOptions(empl);
                    if ('participants' in participantsTabData) {
                        participantsTabData['participants'].options = renamedField;
                        tab = participantsTabData;
                    }
                    dropdownValues['participants'] = renamedField;
                });
                if (Object.keys(res).length > 0) {
                    for (const key in res) {
                        let tab = undefined;
                        let renamedField = renameKeysInOptions(res[key]);
                        if (key in courseDetailsTabData) {
                            courseDetailsTabData[key].options = renamedField;
                            tab = courseDetailsTabData;
                        }
                        dropdownValues[key] = renamedField;
                    }

                    loadTrainingHelpers().then((res) => {
                        for (const key in res) {
                            let tab = undefined;
                            let renamedField = renameKeysInOptions(res[key]);
                            if (key in courseDetailsTabData) {
                                courseDetailsTabData[key].options = renamedField;
                                tab = courseDetailsTabData;
                                setCourseDetailsTabArray(Object.entries(courseDetailsTabData));
                            }
                            dropdownValues[key] = renamedField;
                        }
                    });
                }
            });
            setDropdowns(dropdownValues)
            setCourseDetailsTabArray(Object.entries(courseDetailsTabData));
        }
        setRefreshAfterSave(false);
        setIsLoading(false);
    }, [refreshAfterSave])

    const LoadEvent = async () => {
        try {
            const [eventRes, organizersRes] = await Promise.all([
                loadEvent(selectedTraining.trnUI),
                loadEventOrganizers(selectedTraining.trnUI)
            ]);
            if (eventRes.length > 0) {
                setIsEvent(true);
                setEventData(eventRes[0]);
                setOrganizersData(organizersRes)
            }
        } catch (err) {
            showMessage({
                message: t('common:loadTrainingsError'),
                type: "danger",
                duration: 3000,
            });
        }
    }

    const loadTrainingData = () => {
        loadTrainingDetails(selectedTraining.trnUI).then((trn) => {
            if (trn.length > 0) {
                const userData = trn[0];
                setOriginalUserData(userData);

                const sParams = [
                    { company: { sFilt1: "", sFilt2: account.employeeId, sFilt3: "" } },
                    { branch: { sFilt1: userData.company, sFilt2: "", sFilt3: "" } },
                    { category: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                    { subject: { sFilt1: userData.category, sFilt2: "", sFilt3: "" } }
                ];
                let dropdownValues = {};
                getDropdownValues(sParams).then((res) => {
                    getEmployeesInCompany("").then((empl) => {
                        let renamedField = renameKeysInOptions(empl);
                        if ('participants' in participantsTabData) {
                            participantsTabData['participants'].options = renamedField;
                            tab = participantsTabData;
                        }
                        dropdownValues['participants'] = renamedField;
                    });
                    if (Object.keys(res).length > 0) {
                        for (const key in res) {
                            let tab = undefined;
                            let renamedField = renameKeysInOptions(res[key]);
                            if (key in courseDetailsTabData) {
                                courseDetailsTabData[key].options = renamedField;
                                tab = courseDetailsTabData;
                            }
                            dropdownValues[key] = renamedField;
                        }

                        loadTrainingHelpers().then((res) => {
                            for (const key in res) {
                                let tab = undefined;
                                let renamedField = renameKeysInOptions(res[key]);
                                if (key in courseDetailsTabData) {
                                    courseDetailsTabData[key].options = renamedField;
                                    tab = courseDetailsTabData;
                                    setCourseDetailsTabArray(Object.entries(courseDetailsTabData));
                                }
                                dropdownValues[key] = renamedField;
                            }
                        });
                    }
                });

                loadParticipants(selectedTraining.trnUI).then((res) => {
                    setOriginalPartData(res);
                    totalParticipants(selectedTraining.trnUI).then((res) => {
                        setTotalPart(res[0].column1);
                    });
                });

                setDropdowns(dropdownValues)
                Object.keys(userData).forEach(key => {
                    if (key === 'startDate' || key === 'endDate' || key === 'applicationDate' || key === 'receivedDate' || key === 'doCertExp')
                        setValue(key, formatDateForUI(userData[key] ?? ""))
                    else setValue(key, userData[key] ?? "");
                    register(key);
                });
                setCourseDetailsTabArray(Object.entries(courseDetailsTabData));
            } else {
                showMessage({
                    message: t('common:loadTrainingsError'),
                    type: "danger",
                    duration: 3000,
                })
            }
        })
            .catch(() => {
                setRefreshAfterSave(false);
                setIsLoading(false);
            });
    }
    //#endregion

    //#region Render
    const renderFields = useCallback((dataArr) => {
        return (
            dataArr.map((k) => (
                <DisplayField
                    styles={styles}
                    colors={colors}
                    field={k}
                    control={control}
                    getValues={getValues}
                    setValue={setValue}
                    getDropdownValues={getDropdownValues}
                    renameKeysInOptions={renameKeysInOptions}
                    watch={watch}
                    t={t}
                    employeeId={account.employeeId}
                    totalPart={totalPart}
                />))
        );
    }, [setValue, totalPart]);
    //#endregion

    //#region Functions
    function renameKeysInOptions(options) {
        return options.map(option => {
            const keys = Object.keys(option);
            const newObj = {
                value: option[keys[0]] !== null ? option[keys[0]] : option[keys[1]],
                label: option[keys[1]] !== null ? option[keys[1]] : option[keys[0]]
            };
            return newObj;
        });
    }
    //#endregion

    //#region Save
    const ValidationOfValues = () => {
        // Get mandatory fields
        let title = getValues('title') || "";
        let startDate = getValues('startDate') || "";
        let endDate = getValues('endDate') || "";
        let company = getValues('company') || "";

        // Validate mandatory fields
        if (!title.trim()) {
            showMessage({
                message: t('common:titleReq'),
                type: "danger",
                duration: 3000,
            });
            setIsLoading(false);
            return;
        }

        if (!startDate.trim()) {
            showMessage({
                message: t('common:startDateReq'),
                type: "danger",
                duration: 3000,
            });
            setIsLoading(false);
            return;
        }

        if (!endDate.trim()) {
            showMessage({
                message: t('common:endDateReq'),
                type: "danger",
                duration: 3000,
            });
            setIsLoading(false);
            return;
        }

        if (!company.trim()) {
            showMessage({
                message: t('common:companyReq'),
                type: "danger",
                duration: 3000,
            });
            setIsLoading(false);
            return;
        }

        // Validate that endDate is after startDate
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (end <= start) {
            showMessage({
                message: t('common:dateValidation'),
                type: "danger",
                duration: 3000,
            });
            setIsLoading(false);
            return false;
        }

        return true;
    }

    const onSubmit = async (data) => {
        try {
            setIsLoading(true);
            if (!ValidationOfValues()) return;

            if (!isEdit) {
                if (selectedTraining && selectedTraining.trnUI) {
                    data.trnUI = selectedTraining.trnUI;
                } else {
                    data.trnUI = generateUniqueID();
                    setSelectedTraining(data);
                }

                const checkboxFields = courseDetailsTabArray.filter(([key, field]) => {
                    const types = Array.isArray(field.type) ? field.type : [field.type];
                    return types.includes('checkbox');
                }).map(([key]) => key);

                Object.keys(data).forEach(key => {
                    if (data[key] === undefined) {
                        if (checkboxFields.includes(key)) {
                            data[key] = 0;
                        } else {
                            data[key] = "";
                        }
                    }
                });
            }

            /* make sure dates are in a valid format for save request */
            function convertToISO(dateStr) {
                if (!dateStr) return null;
                const parts = dateStr.split('/');
                if (parts.length !== 3) return dateStr;
                // Rearrange to yyyy-MM-dd and create ISO string
                return new Date(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`).toISOString();
            }

            data.startDate = convertToISO(getValues('startDate'));
            data.endDate = convertToISO(getValues('endDate'));
            data.applicationDate = convertToISO(getValues('applicationDate'));
            data.receivedDate = convertToISO(getValues('receivedDate'));
            data.doCertExp = convertToISO(getValues('doCertExp'));

            const response = await saveTrainingData(data);

            if (response && response === 200) {
                setIsEdit(true);
                setRefreshAfterSave(true)
                showMessage({
                    message: t('common:saveSuccess'),
                    type: "success",
                    duration: 3000,
                });
            } else {
                showMessage({
                    message: t('common:saveFailed'),
                    type: "danger",
                    duration: 3000,
                });
            }

            setIsLoading(false);
        }
        catch (error) {
            setIsLoading(false);
            console.log(error)
        }
    }

    const onErrorsFound = () => {
        showMessage({
            message: t('common:errReqFieldsNotFilled'),
            type: "danger",
            duration: 3000,
        })
    }
    //#endregion

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <>
            <SafeAreaView style={styles.mainView}>
                <ScrollView stickyHeaderIndices={[0]}>
                    {!isParticipantsExpanded && !isModulesExpanded && !isPrerequisitesExpanded && !isEvaluationFormExpanded && !isTrainingMaterialExpanded &&
                        <SafeAreaView style={{
                            flexDirection: "row",
                            justifyContent: "flex-end",
                            alignSelf: "flex-end",
                            marginRight: 15,
                        }}>
                            <CustomButton
                                text={t('common:save')}
                                onPress={handleSubmit(onSubmit, onErrorsFound)}
                                type="BUTTON_SAVE"
                                isGradient={true}
                            />
                            <CustomButton
                                text={t('common:cancel')}
                                onPress={() => setRefreshAfterSave(true)}
                                type="BUTTON_CANCEL"
                            />
                            <CustomButton
                                text={t('common:event')}
                                onPress={() => { openModal() }}
                                type='BUTTON_ADD'
                                textTop='0'
                                sz={15}
                            />
                            {/*<CustomButton
                                text={t('common:delete')}
                                onPress={() => setRefreshAfterSave(true)}
                                type="BUTTON_CANCEL"
                            />*/}
                        </SafeAreaView>
                    }
                    <SafeAreaView style={styles.subMain}>
                        <SafeAreaView style={[common.cardstyle, { margin: 10 }]}>
                            <MemoizedCustomButton
                                onPress={() => toggleTab('course')}
                                t={t}
                                text={t('navigate:courseDetails')}
                                setExpanded={setIsCourseExpanded}
                                isExpanded={isCourseExpanded}
                                styles={styles}
                            />
                            <CustomExpandableView expanded={isCourseExpanded}>
                                <SafeAreaView style={common.divider} />
                                <Render render={renderFields} styles={styles} arr={courseDetailsTabArray} totalPart={totalPart} />
                            </CustomExpandableView>
                        </SafeAreaView>
                        {isEdit &&
                            <>
                                <SafeAreaView style={[common.cardstyle, { margin: 10 }]}>
                                    <MemoizedCustomButton
                                        onPress={() => {
                                            if (isEvent) {
                                                Swal.fire({
                                                    title: t('common:cantAccessPart'),
                                                    text: t('common:cantAccessPartOnEventReason'),
                                                    icon: "warning",
                                                    reverseButtons: true,
                                                    confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">Okay</text>`,
                                                    confirmButtonColor: '#A0A2FF',
                                                }).then((result) => {
                                                    if (result.isConfirmed) {
                                                        return;
                                                    }
                                                });
                                            } else {
                                                toggleTab('participants')
                                            }
                                        }}
                                        t={t}
                                        text={t('navigate:participants')}
                                        setExpanded={setIsParticipantsExpanded}
                                        isExpanded={isParticipantsExpanded}
                                        styles={styles}
                                    />
                                    <CustomExpandableView expanded={isParticipantsExpanded}>
                                        <SafeAreaView style={common.divider} />
                                        <HRAdminTrainingParticipantsScreen
                                            isEdit={isEdit}
                                            data={originalPartData}
                                            trainings={trnOptions}
                                            participantsTabData={participantsTabData}
                                            participantOptions={dropdowns['participants'] || []}
                                            totalPart={totalPart}
                                            selectedTraining={selectedTraining.trnUI}
                                            equalCost={originalUserData.equalCost}
                                            setRefreshAfterSave={setRefreshAfterSave}
                                        />
                                    </CustomExpandableView>
                                </SafeAreaView>
                                <SafeAreaView style={[common.cardstyle, { margin: 10 }]}>
                                    <MemoizedCustomButton
                                        onPress={() => toggleTab('modules')}
                                        t={t}
                                        text={t('navigate:modules')}
                                        setExpanded={setIsModulesExpanded}
                                        isExpanded={isModulesExpanded}
                                        styles={styles}
                                    />
                                    <CustomExpandableView expanded={isModulesExpanded}>
                                        <SafeAreaView style={common.divider} />
                                        <HRAdminTrainingModulesScreen
                                            isEdit={isEdit}
                                            setIsEdit={setIsEdit}
                                            trainings={trnOptions}
                                            trnUI={selectedTraining.trnUI}
                                            employeeOptions={dropdowns['participants'] || []}
                                        />
                                    </CustomExpandableView>
                                </SafeAreaView>
                                <SafeAreaView style={[common.cardstyle, { margin: 10 }]}>
                                    <MemoizedCustomButton
                                        onPress={() => toggleTab('prerequisites')}
                                        t={t}
                                        text={t('navigate:prerequisites')}
                                        setExpanded={setIsPrerequisitesExpanded}
                                        isExpanded={isPrerequisitesExpanded}
                                        styles={styles}
                                    />
                                    <CustomExpandableView expanded={isPrerequisitesExpanded}>
                                        <SafeAreaView style={common.divider} />
                                        <HRAdminPrerequisitesScreen
                                            isEdit={isEdit}
                                            trnUI={selectedTraining.trnUI}
                                            trnData={trnData}
                                        />
                                    </CustomExpandableView>
                                </SafeAreaView>
                                <SafeAreaView style={[common.cardstyle, { margin: 10 }]}>
                                    <MemoizedCustomButton
                                        onPress={() => toggleTab('evaluationForm')}
                                        t={t}
                                        text={t('navigate:trnEvaluationForm')}
                                        setExpanded={setIsEvaluationFormExpanded}
                                        isExpanded={isEvaluationFormExpanded}
                                        styles={styles}
                                    />
                                    <CustomExpandableView expanded={isEvaluationFormExpanded}>
                                        <SafeAreaView style={common.divider} />
                                        <HRAdminDocFormScreen
                                            isEdit={isEdit}
                                            isUser={false}
                                            isAccepted={false}
                                            setIsEdit={setIsEdit}
                                            trnUI={selectedTraining.trnUI}
                                            employeeOptions={dropdowns['participants'] || []}
                                            loadDocForm={loadEvaluationForms}
                                            saveDocForm={SaveEvaluationForm}
                                            DeleteDocForm={DeleteEvaluationForm}
                                        />
                                    </CustomExpandableView>
                                </SafeAreaView>
                                <SafeAreaView style={[common.cardstyle, { margin: 10 }]}>
                                    <MemoizedCustomButton
                                        onPress={() => toggleTab('trainingMaterial')}
                                        t={t}
                                        text={t('navigate:trnMaterial')}
                                        setExpanded={setIsTrainingMaterialExpanded}
                                        isExpanded={isTrainingMaterialExpanded}
                                        styles={styles}
                                    />
                                    <CustomExpandableView expanded={isTrainingMaterialExpanded}>
                                        <SafeAreaView style={common.divider} />
                                        <HRAdminDocFormScreen
                                            isEdit={isEdit}
                                            isUser={false}
                                            isAccepted={false}
                                            trnUI={selectedTraining.trnUI}
                                            employeeOptions={dropdowns['participants'] || []}
                                            loadDocForm={loadTrainingMaterial}
                                            saveDocForm={SaveTrainingMaterial}
                                            DeleteDocForm={DeleteTrainingMaterial}
                                        />
                                    </CustomExpandableView>
                                </SafeAreaView>
                            </>}
                    </SafeAreaView>
                </ScrollView>
            </SafeAreaView>
            {/* Create Event Modal */}
            <Modal
                animationType="slide"
                transparent={true}
                visible={modalVisible}
                onRequestClose={closeModal}
                onBackdropPress={closeModal}
            >
                <SafeAreaView style={styles.modalOverlay}>
                    <SafeAreaView style={styles.modalContainer}>
                        <SafeAreaView style={{ padding: 20 }}>
                            <CustomLabel value={t('common:createEvent')} type="TITLE" style={{ textAlign: 'left', margin: 10 }} />
                        </SafeAreaView>

                        <SafeAreaView style={{ paddingHorizontal: 20 }}>
                            {errorMessage ? (
                                <View style={styles.errorMessageContainer}>
                                    <Text style={styles.errorMessageText}>{errorMessage}</Text>
                                </View>
                            ) : null}
                        </SafeAreaView>

                        <ScrollView>
                            <SafeAreaView style={styles.modalContent}>
                                {/* Training Title */}
                                <View style={styles.modalField}>
                                    <CustomLabel value={t('common:training')} type='MAIN_LABEL_DASHBOARD_MINOR' />
                                    <CustomInput
                                        name="trainingTitle"
                                        editable={!isEvent}
                                        type='MAININPUT'
                                        value={selectedTraining.title}
                                    />
                                </View>

                                {/* Title */}
                                <View style={styles.modalField}>
                                    <CustomLabel value={t('common:title')} type='MAIN_LABEL_DASHBOARD_MINOR' />
                                    <CustomInput
                                        name="title"
                                        editable={!isEvent}
                                        type='MAININPUT'
                                        value={eventData.title || ''}
                                        placeholder={t('common:title')}
                                        setValue={(text) => setEventData(prev => ({ ...prev, title: text }))}
                                    />
                                </View>

                                {/* Description */}
                                <View style={styles.modalField}>
                                    <CustomLabel value={t('common:description')} type='MAIN_LABEL_DASHBOARD_MINOR' />
                                    <CustomInput
                                        name="description"
                                        editable={!isEvent}
                                        type='MAININPUT'
                                        value={eventData.description || ''}
                                        placeholder={t('common:description')}
                                        numberOfLines={3}
                                        style={{ textAlignVertical: 'top' }}
                                        setValue={(text) => setEventData(prev => ({ ...prev, description: text }))}
                                    />
                                </View>

                                {/* Checkboxes in 2x2 grid */}
                                <View style={styles.checkboxGrid}>
                                    <View style={styles.checkboxItem}>
                                        <Checkbox
                                            status={eventData.isActive ? 'checked' : 'unchecked'}
                                            onPress={() => { !isEvent ? setEventData(prev => ({ ...prev, isActive: !prev.isActive })) : {} }}
                                        />
                                        <CustomLabel value={t('common:active')} type='MAIN_LABEL_DASHBOARD_MINOR' style={{ marginLeft: 8 }} />
                                    </View>

                                    <View style={styles.checkboxItem}>
                                        <Checkbox
                                            status={eventData.restrictRegistration ? 'checked' : 'unchecked'}
                                            onPress={() => { !isEvent ? setEventData(prev => ({ ...prev, restrictRegistration: !prev.restrictRegistration })) : {} }}
                                        />
                                        <CustomLabel value={t('common:restrictRegistration')} type='MAIN_LABEL_DASHBOARD_MINOR' style={{ marginLeft: 8 }} />
                                    </View>

                                    <View style={styles.checkboxItem}>
                                        <Checkbox
                                            status={eventData.oneSession ? 'checked' : 'unchecked'}
                                            onPress={() => { !isEvent ? setEventData(prev => ({ ...prev, oneSession: !prev.oneSession })) : {} }}
                                        />
                                        <CustomLabel value={t('common:limitOnePersonPerParticipant')} type='MAIN_LABEL_DASHBOARD_MINOR' style={{ marginLeft: 8 }} />
                                    </View>

                                    <View style={styles.checkboxItem}>
                                        <Checkbox
                                            status={eventData.autoConfirm ? 'checked' : 'unchecked'}
                                            onPress={() => { !isEvent ? setEventData(prev => ({ ...prev, autoConfirm: !prev.autoConfirm })) : {} }}
                                        />
                                        <CustomLabel value={t('common:confirmParticipantsAutomatically')} type='MAIN_LABEL_DASHBOARD_MINOR' style={{ marginLeft: 8 }} />
                                    </View>
                                </View>

                                {/* Organizers Label with Divider */}
                                <View style={{ marginTop: 20 }}>
                                    <SafeAreaView style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                        <CustomLabel value={t('common:organizers')} type='EDIT_EMPLOYEE_DIVIDER' />
                                        {!isEvent && <CustomButton
                                            text={t('common:addOrg')}
                                            onPress={() => { handleAddNew() }}
                                            type='BUTTON_ADD'
                                            textTop='0'
                                            sz={15}
                                        />}
                                    </SafeAreaView>
                                    <SafeAreaView style={common.divider} />
                                    <FlatList
                                        data={organizersData}
                                        keyExtractor={(item) => item.rowguid}
                                        scrollEnabled={true}
                                        initialNumToRender={4}
                                        renderItem={({ item, index }) =>
                                            renderOrganizers(item, index)
                                        }
                                    />
                                </View>
                            </SafeAreaView>
                        </ScrollView>
                        {/* Bottom Buttons */}
                        <SafeAreaView style={styles.modalButtonsContainer}>
                            <CustomButton
                                text={isEvent ? t('common:close') : t('common:cancel')}
                                onPress={() => {
                                    setRefreshAfterSave();
                                    closeModal()
                                }}
                                type="BUTTON_CANCEL"
                                sz={15}
                                style={{ marginRight: 10 }}
                            />
                            {!isEvent && <CustomButton
                                text={t('common:save')}
                                onPress={() => { saveEvent() }}
                                type="BUTTON_SAVE"
                                sz={15}
                                isGradient={true}
                            />}
                        </SafeAreaView>
                    </SafeAreaView>
                </SafeAreaView>
            </Modal>
        </>
    );
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
        height: '100%',
    },
    subMain: {
        flex: 1,
        marginHorizontal: 10,
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
        paddingHorizontal: 5,
        justifyContent: 'space-around',
    },
    rowOrg: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
    },
    itemOrg: {
        flex: 1,
    },
    column: {
        width: '40%',
        height: '100%',
        marginBottom: 10
    },
    verticalLine: {
        backgroundColor: colors.background_gray,
        width: 3,
        height: '100%',
        marginHorizontal: '2%'
    },
    horizontalLine: {
        backgroundColor: colors.background_gray,
        width: '100%',
        height: 1.5,
        marginBottom: 3
    },
    box: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
        marginTop: 5,
        flex: 1
    },
    icons: {
        fontSize: 25,
        color: colors.text_primary,
        margin: '5%'
    },
    leftColumn: {
        width: '25%'
    },
    middleColumn: {
        width: '60%'
    },
    rightColumn: {
        width: '3%',
    },
    label: {
        flex: 1,
        textAlign: 'left',
    },
    input: {
        flex: 1,
        marginVertical: '2%'
    },
    rowFlatList: {
        flexDirection: 'row',
    },
    nameColumn: {
        width: '40%',
    },
    itemFlatlist: {
        flex: 1,
        margin: 10
    },
    rowHeaders: {
        flexDirection: "row",
        marginHorizontal: '1%',
    },
    verticalline: {
        backgroundColor: colors.buttons_primary,
        height: '100%',
        width: 1,
    },
    checkboxColumn: {
        width: '12%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    inputColumn: {
        flex: 1,
        minWidth: '20%',
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        width: '60%',
        height: '70%',
        backgroundColor: 'white',
        borderRadius: 10,
        padding: 20,
    },
    errorMessageContainer: {
        backgroundColor: colors.background,
        padding: 10,
        borderRadius: 5,
        marginVertical: 10,
    },
    errorMessageText: {
        color: colors.validation_error,
        fontWeight: 'bold',
    },
    modalContent: {
        paddingHorizontal: 10,
        paddingBottom: 20,
    },
    modalField: {
        flexDirection: 'row',
        margin: 15,
    },
    checkboxGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        margin: 15,
    },
    checkboxItem: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '48%',
        marginBottom: 12,
    },
    modalButtonsContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        paddingHorizontal: 20,
        paddingVertical: 15,
        marginTop: 10,
    },
});
//#endregion

export default HRAdminTrainingDetailsScreen;